import React, { useState, useEffect, useRef } from 'react';
import { FiMessageCircle, FiX, FiArrowLeft } from 'react-icons/fi';
import './Messaging.css';
import {
  API_BASE_URL,
  API_SOCKET_URL,
  redirectToLoginWithReturn,
  toastdisplay,
  updateConversationWithMessage,
  convertToLocalTime,
  getAuthHeaders,
} from '../../utils/utils';
import { createAuthenticatedWebSocket } from '../../utils/authSession';
import ProfilePicture from '../../components/ProfilePicture';

// Helper function to format conversation dates
const formatConversationDate = (dateString) => {
  if (!dateString) return '';

  // Check if dateString is just a time (e.g., "17:04")
  if (typeof dateString === 'string' && dateString.match(/^\d{1,2}:\d{2}$/)) {
    return dateString; // Return the time as-is
  }

  // Check if it's a valid date string
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    // If invalid date, try to parse as time or return the original string
    return dateString;
  }

  const now = new Date();
  const diffTime = Math.abs(now - date);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 1) {
    return 'Today';
  } else if (diffDays === 2) {
    return 'Yesterday';
  } else if (diffDays <= 7) {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  } else {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
};

// WebSocket hook for real-time messaging
const useWebSocket = (userEmail, userId) => {
  const [ws, setWs] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionAttempts, setConnectionAttempts] = useState(0);
  const maxReconnectAttempts = 5;

  useEffect(() => {
    if (!userEmail) {
      console.log('WebSocket: Missing userEmail', {
        userEmail,
      });
      return;
    }

    // Create WebSocket connection - ensure userId is properly converted to integer
    const wsUrl = `${API_SOCKET_URL}/api/ws/user/${encodeURIComponent(userEmail)}`;
    console.log('WebSocket: Attempting to connect to:', wsUrl);

    const websocket = createAuthenticatedWebSocket(wsUrl);
    if (!websocket) {
      return undefined;
    }

    websocket.onopen = () => {
      console.log('WebSocket connected for user:', userEmail);
      console.log('WebSocket URL:', wsUrl);
      setIsConnected(true);
      setConnectionAttempts(0); // Reset connection attempts on successful connection
    };

    websocket.onclose = (event) => {
      console.log(
        'WebSocket disconnected for user:',
        userEmail,
        'Code:',
        event.code,
        'Reason:',
        event.reason
      );
      setIsConnected(false);

      // Attempt to reconnect if not a normal closure and under max attempts
      if (event.code !== 1000 && connectionAttempts < maxReconnectAttempts) {
        const delay = Math.min(1000 * Math.pow(2, connectionAttempts), 10000); // Exponential backoff, max 10s
        console.log(
          `WebSocket: Attempting to reconnect in ${delay}ms (attempt ${connectionAttempts + 1}/${maxReconnectAttempts})`
        );

        setTimeout(() => {
          setConnectionAttempts((prev) => prev + 1);
        }, delay);
      }
    };

    websocket.onerror = (error) => {
      console.error('WebSocket error for user:', error);
      console.error('WebSocket error details:', {
        wsUrl,
        error: error.message || error,
      });
      setIsConnected(false);
    };

    setWs(websocket);

    // Cleanup on unmount
    return () => {
      if (websocket) {
        websocket.close(1000, 'Component unmounting');
      }
    };
  }, [userEmail, connectionAttempts]);

  return { ws, isConnected };
};

export function MessagingButton({ onClick, newCount, showBadge, isConnected }) {
  return (
    <div className="messaging-button-container">
      <button
        className={`liwaza-messaging-fab ${isConnected ? 'connected' : 'disconnected'}`}
        onClick={onClick}
        aria-label="Open messaging"
      >
        <FiMessageCircle size={24} />
      </button>
      {newCount > 0 && showBadge && (
        <span className="liwaza-messaging-badge">
          {newCount > 99 ? '99+' : newCount}
        </span>
      )}
      {/* Connection status indicator */}
      <div
        className={`connection-indicator ${isConnected ? 'connected' : 'disconnected'}`}
        title={
          isConnected
            ? 'Real-time messaging connected'
            : 'Real-time messaging disconnected'
        }
      >
        <div className="connection-dot"></div>
      </div>
    </div>
  );
}

export function MessagingPanel({
  userRole,
  userEmail,
  userName,
  conversations = [],
  selectedConversation,
  setSelectedConversation,
  setConversations,
  onClose,
  isConnected, // Add connection status prop
  isLoadingConversations,
}) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showConversationView, setShowConversationView] = useState(false);

  // Reset mobile state when panel opens/mounts
  useEffect(() => {
    const mobile = window.innerWidth <= 768;
    setIsMobile(mobile);

    if (mobile) {
      // On mobile, always start with conversation list view
      setShowConversationView(false);
    } else if (selectedConversation) {
      // On desktop, show conversation view if conversation is selected
      setShowConversationView(true);
    }
  }, []); // Run only on mount (when panel opens)

  // Auto-focus on conversation when externally selected
  useEffect(() => {
    if (selectedConversation) {
      if (isMobile) {
        setShowConversationView(true);
      } else {
        setShowConversationView(true);
      }
    }
  }, [selectedConversation, isMobile]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);

      // On desktop, always show conversation view if conversation is selected
      if (!mobile && selectedConversation) {
        setShowConversationView(true);
      }
      // On mobile, only show conversation view if explicitly set
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [selectedConversation]);

  // Handle conversation selection changes
  useEffect(() => {
    if (!isMobile && selectedConversation) {
      // On desktop, always show conversation when selected
      setShowConversationView(true);
    } else if (isMobile && !selectedConversation) {
      // On mobile, go back to list when conversation is cleared
      setShowConversationView(false);
    }
  }, [selectedConversation, isMobile]);

  // Cleanup when component unmounts (panel closes)
  useEffect(() => {
    return () => {
      // Reset conversation view state when panel closes
      // This provides extra safety for state management
      setShowConversationView(false);
    };
  }, []);

  const handleSelectConversation = (conv) => {
    if (!conversations || !Array.isArray(conversations)) {
      console.warn('Conversations not available for selection');
      return;
    }

    const updated = conversations.map((c) =>
      c.id === conv.id
        ? {
            ...c,
            messages:
              c.messages && Array.isArray(c.messages)
                ? c.messages.map((m) => ({ ...m, read: true }))
                : [],
          }
        : c
    );
    setConversations(updated);
    setSelectedConversation(conv);

    // On mobile, show conversation view when conversation is selected
    if (isMobile) {
      setShowConversationView(true);
    }
  };

  const handleBackToList = () => {
    if (isMobile) {
      setShowConversationView(false);
      // Optionally clear selected conversation on mobile for cleaner state
      // setSelectedConversation(null);
    }
  };

  return (
    <div className="liwaza-messaging-panel">
      <div className="messaging-header">
        {isMobile && showConversationView && selectedConversation ? (
          <>
            <button
              className="messaging-back-button"
              onClick={handleBackToList}
              aria-label="Back to conversations"
            >
              <FiArrowLeft size={20} />
            </button>
            <span className="messaging-title">{selectedConversation.name}</span>
          </>
        ) : (
          <span className="messaging-title">Messaging</span>
        )}
        <button className="messaging-close" onClick={onClose}>
          <FiX size={22} />
        </button>
      </div>
      <div className="messaging-body">
        <ConversationList
          userRole={userRole}
          conversations={conversations}
          selectedConversation={selectedConversation}
          handleSelectConversation={handleSelectConversation}
          isMobile={isMobile}
          showConversationView={showConversationView}
        />
        <ConversationView
          conversation={selectedConversation}
          setConversation={setSelectedConversation}
          userRole={userRole}
          userEmail={userEmail}
          userName={userName}
          setConversations={setConversations}
          conversations={conversations}
          isMobile={isMobile}
          showConversationView={showConversationView}
          onBackToList={handleBackToList}
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// List of Conversations
function ConversationList({
  conversations = [],
  selectedConversation,
  handleSelectConversation,
  isMobile,
  showConversationView,
  isLoadingConversations,
}) {
  return (
    <div
      className={`conversation-list ${isMobile && showConversationView ? 'mobile-hidden' : ''}`}
    >
      <div className="conversation-list-items">
        {conversations.length === 0 ? (
          <div style={{ padding: '24px', color: '#6c757d', fontSize: 14 }}>
            No conversations yet. You'll see messages here when you receive
            them.
          </div>
        ) : (
          conversations.map((conv) => (
            <div
              key={conv.id}
              className={`conversation-list-item${selectedConversation?.id === conv.id ? ' selected' : ''}`}
              onClick={() => handleSelectConversation(conv)}
            >
              <ProfilePicture
                user={{
                  profile_picture: conv.avatar,
                  email: conv.email || conv.name,
                  id: conv.id,
                }}
                alt={conv.name}
                context="Messaging conversation list"
                className="conversation-avatar"
              />
              <div>
                <div className="conversation-name">
                  {typeof conv.name === 'string' ? conv.name : 'Unknown User'}
                </div>
                <div className="conversation-preview">
                  {typeof conv.lastMessage === 'string'
                    ? conv.lastMessage
                    : 'No messages yet'}
                </div>
              </div>
              <div className="conversation-date">
                <div>{formatConversationDate(conv.date)}</div>
                {conv.messages && conv.messages.length > 0 && (
                  <div className="conversation-time">
                    {conv.messages[conv.messages.length - 1]?.displayTime ||
                      convertToLocalTime(
                        conv.messages[conv.messages.length - 1]?.date,
                        conv.messages[conv.messages.length - 1]?.senderTimezone
                      )}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function ConversationView({
  conversation,
  userRole,
  userEmail,
  userName,
  setConversation,
  setConversations,
  conversations = [],
  isMobile,
  showConversationView,
  onBackToList,
}) {
  const [message, setMessage] = useState('');
  const messagesEndRef = useRef(null);
  const [toast, setToast] = useState({
    show: false,
    message: '',
    success: true,
  });
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation?.messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    await sendMessage();
  };

  const sendMessage = async () => {
    if (!message.trim()) return;

    const timestamp = new Date().toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });

    try {
      // Determine if this is a direct message or mentorship request message
      const isDirectMessage = !conversation.request_id;

      const endpoint = '/api/messages';

      // Get user's timezone
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const requestBody = isDirectMessage
        ? {
            sender_email: userEmail,
            receiver_email: conversation.receiver_email || conversation.email,
            content: message,
            sender_timezone: userTimezone,
          }
        : {
            request_id: conversation.request_id,
            sender_email: userEmail,
            receiver_email: conversation.receiver_email,
            content: message,
            sender_timezone: userTimezone,
          };

      // Create the new message object with timezone information
      const newMsg = {
        author: userName,
        fromMe: true,
        text: message,
        date: new Date().toISOString(), // Keep full date for proper sorting
        displayTime: convertToLocalTime(new Date().toISOString(), userTimezone), // Convert to local time
        senderTimezone: userTimezone, // Include sender's timezone
        serverTime: new Date().toISOString(), // Include server time for accurate sorting
        read: true,
      };

      // Optimistically update the UI immediately for better user experience
      const updatedConversations = conversations.map((conv) =>
        conv.id === conversation.id
          ? {
              ...conv,
              messages: [...conv.messages, newMsg],
              lastMessage: message,
              date: new Date().toISOString(),
            }
          : conv
      );

      const updatedConversation = updatedConversations.find(
        (c) => c.id === conversation.id
      );

      // Update the UI immediately
      setConversation(updatedConversation);
      setConversations(updatedConversations);
      setMessage('');

      // Now send to backend
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        if (response.status === 401) {
          setSessionExpired(true);
          redirectToLoginWithReturn({
            message: 'Votre session a expire. Veuillez vous reconnecter.',
            setToast,
          });
          return;
        }

        // If backend fails, revert the optimistic update
        const errorData = await response.json().catch(() => ({}));

        // Handle different error data structures safely
        let errorMessage = 'Failed to send message';
        if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (
            typeof errorData.detail === 'object' &&
            errorData.detail.msg
          ) {
            errorMessage = errorData.detail.msg;
          } else if (Array.isArray(errorData.detail)) {
            errorMessage = errorData.detail[0]?.msg || 'Failed to send message';
          }
        }

        // Revert to previous state
        setConversation(conversation);
        setConversations(conversations);

        toastdisplay(errorMessage, false, 2000, setToast);
        return;
      }

      const responseData = await response.json();
      console.log('Message sent successfully:', responseData);

      // Show final success message
      toastdisplay('Message sent successfully', true, 2000, setToast);
    } catch (error) {
      if (error?.message === 'Authentication failed') {
        setSessionExpired(true);
        redirectToLoginWithReturn({
          message: 'Votre session a expire. Veuillez vous reconnecter.',
          setToast,
        });
        return;
      }
      console.error('Send message error:', error);
      toastdisplay('Server error', false, 2000, setToast);
    }
  };

  if (!conversation)
    return <div className="conversation-view-empty">Select a conversation</div>;

  // Determine the CSS classes for mobile behavior
  const viewClassName = `conversation-view ${
    isMobile && showConversationView ? 'active' : ''
  }`;

  return (
    <div className={viewClassName}>
      <div className="conversation-view-header">
        <ProfilePicture
          user={{
            profile_picture: conversation.avatar,
            email: conversation.email || conversation.name,
            id: conversation.id,
          }}
          alt={
            typeof conversation.name === 'string'
              ? conversation.name
              : 'User Avatar'
          }
          context="Messaging conversation header"
          className="conversation-view-avatar"
        />
        <div>
          <div className="conversation-view-name">{conversation.name}</div>
          <div className="conversation-view-status">{conversation.status}</div>
        </div>
      </div>
      <div className="conversation-messages">
        {conversation.messages.map((msg, idx) => (
          <div
            key={idx}
            className={`conversation-message${msg.fromMe ? ' from-me' : ''}${msg.isBookingMessage ? ' booking-message' : ''}`}
          >
            {msg.isBookingMessage && msg.bookingContext && (
              <div className="booking-context-label">
                📅 {msg.bookingContext}
              </div>
            )}
            <div className="message-author">{msg.author}</div>
            <div className="message-content">{msg.text}</div>
            <div className="message-date">
              {msg.displayTime || msg.date}
              {msg.senderTimezone &&
                msg.senderTimezone !==
                  Intl.DateTimeFormat().resolvedOptions().timeZone && (
                  <span className="message-timezone">
                    {' '}
                    ({msg.senderTimezone})
                  </span>
                )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      <form className="conversation-input-row" onSubmit={handleSend}>
        <textarea
          className="conversation-input"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              sendMessage();
            }
          }}
          placeholder="Write a message..."
          rows={2}
        />
        <button
          className="btn-lwz-primary"
          type="submit"
          disabled={!message.trim()}
        >
          Send
        </button>
      </form>
      {toast.show && (
        <div
          className={`toast-notification ${toast.success ? 'success' : 'error'}`}
        >
          <span className="toast-icon">{toast.success ? '✔️' : '❌'}</span>
          <span>{toast.message}</span>
        </div>
      )}
    </div>
  );
}

export function CallMessaging({
  conversations = [],
  setConversations,
  showMessaging,
  userRole,
  userEmail,
  userName,
  userId, // Add userId prop for WebSocket connection
  // External control props (optional)
  externalShowPanel,
  externalSetShowPanel,
  externalSelectedConversation,
  externalSetSelectedConversation,
  onClose,
}) {
  const [internalShowPanel, setInternalShowPanel] = useState(false);
  const [internalSelectedConversation, setInternalSelectedConversation] =
    useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const [wsToast, setWsToast] = useState({
    show: false,
    message: '',
    success: true,
  });

  // Initialize WebSocket connection
  const { ws, isConnected } = useWebSocket(userEmail, userId);

  // Show connection status in console for debugging
  useEffect(() => {
    if (userEmail) {
      console.log('WebSocket connection status:', {
        isConnected,
        userEmail,
        ws: ws ? 'WebSocket instance created' : 'No WebSocket instance',
      });

      // Test WebSocket connection
      if (ws && isConnected) {
        console.log('WebSocket is ready for messages');
        // You can add a test message here if needed
      }
    } else {
      console.warn('WebSocket: No userEmail provided');
    }
  }, [isConnected, userEmail, ws]);

  // Handle incoming WebSocket messages
  useEffect(() => {
    if (!ws) return;

    const handleMessage = (event) => {
      try {
        console.log('WebSocket message received:', event.data);
        const data = JSON.parse(event.data);

        if (data.type === 'direct_message') {
          console.log('Received direct message:', data);

          // Ensure conversations is properly initialized
          if (!conversations || !Array.isArray(conversations)) {
            console.log(
              'Conversations not properly initialized:',
              conversations
            );
            return;
          }

          console.log('Current conversations state:', {
            count: conversations.length,
            conversations: conversations.map((c) => ({
              id: c.id,
              name: c.name,
              email: c.email,
              receiver_email: c.receiver_email,
              messageCount: c.messages?.length || 0,
            })),
          });

          // Create the new message object with timezone-aware date handling
          const newMessage = {
            author: data.sender_name,
            fromMe: false,
            text: data.content,
            date: data.server_time || data.created_at, // Use server time for accurate sorting
            displayTime: convertToLocalTime(
              data.created_at,
              data.sender_timezone
            ),
            senderTimezone: data.sender_timezone,
            serverTime: data.server_time,
            read: false,
          };
          console.log('New message:', newMessage);

          // Use the helper function from utils.js to update conversations
          setConversations((prevConversations) => {
            console.log(
              'Previous conversations before update:',
              prevConversations
            );
            console.log('Calling updateConversationWithMessage with:', {
              prevConversations: prevConversations.length,
              newMessage,
              senderEmail: data.sender_email,
              senderName: data.sender_name,
            });

            const updatedConversations = updateConversationWithMessage(
              prevConversations,
              newMessage,
              data.sender_email,
              data.sender_name
            );

            console.log(
              'Updated conversations returned:',
              updatedConversations
            );

            // If this conversation is currently selected, update it
            const updatedConversation = updatedConversations.find(
              (c) =>
                c.email === data.sender_email ||
                c.receiver_email === data.sender_email ||
                c.name === data.sender_name
            );

            console.log('Found updated conversation:', updatedConversation);
            console.log(
              'Current internal selected conversation:',
              internalSelectedConversation
            );
            console.log(
              'Current external selected conversation:',
              externalSelectedConversation
            );

            if (updatedConversation) {
              console.log('Updating selected conversations...');

              // Update internal selected conversation if it matches
              if (internalSelectedConversation?.id === updatedConversation.id) {
                console.log('Updating internal selected conversation');
                setInternalSelectedConversation(updatedConversation);
              }

              // Update external selected conversation if it matches
              if (externalSelectedConversation?.id === updatedConversation.id) {
                console.log('Updating external selected conversation');
                if (externalSetSelectedConversation) {
                  externalSetSelectedConversation(updatedConversation);
                }
              }
            } else {
              console.log(
                'No updated conversation found - this might be the issue!'
              );
            }

            return updatedConversations;
          });

          // Show notification toast
          toastdisplay(
            `New message from ${data.sender_name}`,
            true,
            3000,
            setWsToast
          );
        } else {
          console.log(
            'Received WebSocket message with unknown type:',
            data.type
          );
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
        console.error('Raw message data:', event.data);
      }
    };

    ws.addEventListener('message', handleMessage);

    return () => {
      ws.removeEventListener('message', handleMessage);
    };
  }, [
    ws,
    setConversations,
    setInternalSelectedConversation,
    internalSelectedConversation,
    conversations,
  ]);

  // Use external control if provided, otherwise use internal state
  const showPanel =
    externalShowPanel !== undefined ? externalShowPanel : internalShowPanel;
  const setShowPanel = externalSetShowPanel || setInternalShowPanel;
  const selectedConversation =
    externalSelectedConversation !== undefined
      ? externalSelectedConversation
      : internalSelectedConversation;
  const setSelectedConversation =
    externalSetSelectedConversation || setInternalSelectedConversation;

  useEffect(() => {
    if (!conversations || !Array.isArray(conversations)) {
      setUnreadCount(0);
      return;
    }

    const hasUnread = conversations.some(
      (c) =>
        c.messages &&
        Array.isArray(c.messages) &&
        c.messages.some((m) => !m.fromMe && !m.read)
    );
    if (userRole === 'coach' && hasUnread && !selectedConversation) {
      const firstUnread = conversations.find(
        (c) =>
          c.messages &&
          Array.isArray(c.messages) &&
          c.messages.some((m) => !m.fromMe && !m.read)
      );
      if (firstUnread) setSelectedConversation(firstUnread);
    }

    const count = conversations.reduce(
      (sum, c) =>
        sum +
        (c.messages && Array.isArray(c.messages)
          ? c.messages.filter((m) => !m.fromMe && !m.read).length
          : 0),
      0
    );
    setUnreadCount(count);
  }, [conversations, userRole, selectedConversation]);

  // Auto-select the first conversation when conversations are loaded and no conversation is selected
  useEffect(() => {
    if (
      conversations &&
      Array.isArray(conversations) &&
      conversations.length > 0 &&
      !selectedConversation
    ) {
      // Select the first conversation (top of the list)
      setSelectedConversation(conversations[0]);
    }
  }, [conversations, selectedConversation, setSelectedConversation]);

  // Handle panel close with mobile state reset
  const handleClosePanel = () => {
    if (onClose) {
      // Use external close handler if provided
      onClose();
    } else {
      // Default behavior
      setShowPanel(false);

      // On mobile, clear selected conversation when panel closes
      // This ensures the panel starts fresh with conversation list view next time
      if (window.innerWidth <= 768) {
        setSelectedConversation(null);
      }
    }
  };

  return (
    <>
      {showMessaging && (
        <MessagingButton
          onClick={() => setShowPanel(true)}
          newCount={unreadCount}
          showBadge={unreadCount > 0 && !showPanel}
          isConnected={isConnected}
        />
      )}
      {showPanel && (
        <MessagingPanel
          userRole={userRole}
          userEmail={userEmail}
          userName={userName}
          conversations={conversations}
          selectedConversation={selectedConversation}
          setSelectedConversation={setSelectedConversation}
          setConversations={setConversations}
          onClose={handleClosePanel}
          isConnected={isConnected}
          isLoadingConversations={isLoadingConversations}
        />
      )}

      {wsToast.show && (
        <div
          className={`toast-notification ${wsToast.success ? 'success' : 'error'}`}
        >
          {wsToast.message}
        </div>
      )}
    </>
  );
}
